import { type SignInIdentifier } from '@logto/schemas';
import { t } from 'i18next';
import { useCallback, useContext } from 'react';
import { useTimer } from 'react-timer-hook';

import CaptchaContext from '@/Providers/CaptchaContextProvider/CaptchaContext';
import { sendMfaVerificationCode } from '@/apis/experience';
import useApi from '@/hooks/use-api';
import useErrorHandler from '@/hooks/use-error-handler';
import useToast from '@/hooks/use-toast';
import { requestFreshCaptchaProof } from '@/utils/request-fresh-captcha';

export const timeRange = 59;

const getTimeout = () => {
  const now = new Date();
  now.setSeconds(now.getSeconds() + timeRange);
  return now;
};

const useResendMfaVerificationCode = (
  identifierType: SignInIdentifier.Email | SignInIdentifier.Phone
) => {
  const { setToast } = useToast();
  const handleError = useErrorHandler();
  const { executeCaptcha, isCaptchaRequired } = useContext(CaptchaContext);
  const resend = useApi(sendMfaVerificationCode);

  const { seconds, isRunning, restart } = useTimer({
    autoStart: true,
    expiryTimestamp: getTimeout(),
  });

  const onResendVerificationCode = useCallback(async () => {
    const captchaProof = await requestFreshCaptchaProof({
      isCaptchaRequired,
      executeCaptcha,
    });

    if (!captchaProof.token) {
      if (captchaProof.errorMessage) {
        setToast(captchaProof.errorMessage);
      }

      return;
    }

    const [error, result] = await resend(identifierType, captchaProof.token);

    if (error) {
      await handleError(error, {
        ...(identifierType === 'phone' && {
          'request.rate_limited': () => {
            setToast(t('error.sms_rate_limited'));
          },
          'request.message_rate_limited': () => {
            setToast(t('error.sms_rate_limited'));
          },
        }),
      });
      return;
    }

    if (result) {
      setToast(t('description.passcode_sent'));
      restart(getTimeout(), true);
    }

    return result?.verificationId;
  }, [executeCaptcha, handleError, identifierType, isCaptchaRequired, resend, restart, setToast]);

  return {
    seconds,
    isRunning,
    onResendVerificationCode,
  };
};

export default useResendMfaVerificationCode;
